import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { X, MessageCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { rp } from "@/lib/format";

/**
 * Popup pilih admin saat tombol chat diklik.
 * Data admin dari /settings/public (admin_wa_numbers + admin_wa_labels).
 * Fallback: kalau settings gagal / cuma 1 nomor → langsung buka wa_url produk.
 */
export default function ChatAdminSheet({ product, onClose }) {
  const open = !!product;
  const [opening, setOpening] = useState(null);

  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  if (!open) return null;

  const numbers = settings?.admin_wa_numbers || [];
  const labels = settings?.admin_wa_labels || [];

  const openAdmin = (num, idx) => {
    setOpening(idx);
    const text = encodeURIComponent(
      `Halo ${labels[idx] || "Admin"}! Saya mau tanya barang ini:\n\n${product?.nama}\n${rp(product?.harga_idr || 0)}\n\nMasih ada?`
    );
    window.open(`https://wa.me/${num}?text=${text}`, "_blank");
    setTimeout(() => { setOpening(null); onClose?.(); }, 300);
  };

  const fallbackDirect = () => {
    if (product?.wa_url) window.open(product.wa_url, "_blank");
    onClose?.();
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[70] bg-plum/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        data-testid="chat-admin-backdrop"
      />
      <div
        role="dialog"
        aria-label="Pilih admin"
        className="fixed inset-x-0 bottom-0 z-[80] rounded-t-[32px] bg-cream dark:bg-plum text-plum dark:text-cream shadow-[0_-20px_60px_rgba(74,44,78,0.35)] animate-sheet-up"
        data-testid="chat-admin-sheet"
      >
        <div className="flex justify-center pt-3">
          <span className="h-1.5 w-12 rounded-full bg-plum/25 dark:bg-cream/25" />
        </div>

        <div className="flex items-center justify-between px-6 pt-3 pb-2">
          <h2 className="font-serif italic text-2xl">Chat siapa? 💬</h2>
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-full grid place-items-center bg-plum/10 dark:bg-cream/10 active:scale-95 transition"
            aria-label="Tutup"
            data-testid="chat-admin-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="px-6 pb-4 text-[11px] uppercase tracking-[0.2em] text-plum/50 dark:text-cream/50">
          {product?.nama}
        </p>

        <div className="px-6 pb-8 pb-[max(2rem,env(safe-area-inset-bottom))] flex flex-col gap-3">
          {numbers.length >= 2 ? (
            numbers.map((num, i) => (
              <button
                key={num}
                onClick={() => openAdmin(num, i)}
                disabled={opening !== null}
                data-testid={`chat-admin-${i}`}
                className="w-full h-14 rounded-full flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold text-[15px] shadow-[0_10px_30px_rgba(37,211,102,0.4)] active:scale-[0.98] transition disabled:opacity-60"
              >
                {opening === i ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageCircle className="h-5 w-5" />}
                <span>Chat {labels[i] || `Admin ${i + 1}`}</span>
              </button>
            ))
          ) : (
            <button
              onClick={fallbackDirect}
              data-testid="chat-admin-direct"
              className="w-full h-14 rounded-full flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold text-[15px] shadow-[0_10px_30px_rgba(37,211,102,0.4)] active:scale-[0.98] transition"
            >
              <MessageCircle className="h-5 w-5" />
              <span>Chat Admin</span>
            </button>
          )}
          <p className="text-center text-[10px] uppercase tracking-[0.2em] text-plum/50 dark:text-cream/50">
            Admin fast respon ⚡
          </p>
        </div>
      </div>
    </>,
    document.body
  );
}
